import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import i18next from 'i18next';
import { useFormContext } from 'react-hook-form';
import { Capacitor } from '@capacitor/core';
import { Box } from '@famly/mf_layout_box';
import { Stack } from '@famly/mf_layout_stack';
import { Text } from '@famly/mf_data-display_text';
import { Button } from '@famly/mf_input_button';
import { FormRow } from '@famly/stat_forms_form-row';
import { captureNativePhoto, getWebCameraStream, captureWebPhoto } from './capture-photo';
import { kinderConnectPhotoCaptureButtonE2eId, kinderConnectPhotoRetakeButtonE2eId, kinderConnectPhotoRetryButtonE2eId, } from './kinder-connect-capture-e2e-ids';
const isNative = Capacitor.isNativePlatform();
export const CustomPhotoCaptureRow = ({ isDisabled }) => {
    const { setValue } = useFormContext();
    const [photoState, setPhotoState] = useState(isNative ? { status: 'previewing' } : { status: 'starting' });
    // Stream stored in a ref so the callback ref below can read it synchronously
    const streamRef = useRef(null);
    const stopStream = () => {
        var _a;
        (_a = streamRef.current) === null || _a === void 0 ? void 0 : _a.getTracks().forEach(track => track.stop());
        streamRef.current = null;
    };
    // Opens the web camera stream. Stops any existing stream first to avoid leaks on retake.
    // Status is set to 'previewing' only AFTER the stream is stored in streamRef,
    // so the callback ref below always sees it. Kept setState-free outside the async
    // callbacks so the mount effect below can call it directly without tripping
    // react-hooks/set-state-in-effect.
    const beginWebCameraStream = useCallback(() => {
        stopStream();
        getWebCameraStream()
            .then(stream => {
            streamRef.current = stream;
            setPhotoState({ status: 'previewing' });
        })
            .catch(() => {
            setPhotoState({ status: 'error', kind: 'unknown' });
        });
    }, []);
    // Same as beginWebCameraStream, but also flips the status to 'starting' up front —
    // for use from event handlers (retry button, retake) where the state is not already
    // 'starting'.
    const startWebCamera = useCallback(() => {
        setPhotoState({ status: 'starting' });
        beginWebCameraStream();
    }, [beginWebCameraStream]);
    useEffect(() => {
        if (isNative || isDisabled) {
            return;
        }
        beginWebCameraStream();
        return stopStream;
    }, [isDisabled, beginWebCameraStream]);
    // videoNodeRef lets handleWebCapture access the video element without querySelector.
    const videoNodeRef = useRef(null);
    // Callback ref: called when the video element mounts. By this point
    // streamRef.current is already set (we only render the video in 'previewing'),
    // so srcObject can be assigned synchronously — no race condition.
    const videoCallbackRef = useCallback((node) => {
        videoNodeRef.current = node;
        if (node && streamRef.current) {
            node.srcObject = streamRef.current;
            node.play().catch((_error) => {
                // Autoplay policy may block playback on some browsers — not a fatal error
            });
        }
    }, []);
    const handleNativeCapture = useCallback(() => {
        setPhotoState({ status: 'starting' });
        captureNativePhoto()
            .then(base64 => {
            setValue('photoBase64', base64, { shouldValidate: true });
            setPhotoState({ status: 'captured', dataUrl: `data:image/png;base64,${base64}` });
        })
            .catch(() => {
            setPhotoState({ status: 'previewing' });
        });
    }, [setValue]);
    const handleWebCapture = useCallback((videoElement) => {
        const base64 = captureWebPhoto(videoElement);
        setValue('photoBase64', base64, { shouldValidate: true });
        setPhotoState({ status: 'captured', dataUrl: `data:image/png;base64,${base64}` });
        stopStream();
    }, [setValue]);
    const handleRetake = useCallback(() => {
        setValue('photoBase64', '', { shouldValidate: true });
        if (isNative) {
            setPhotoState({ status: 'previewing' });
        }
        else {
            startWebCamera();
        }
    }, [setValue, startWebCamera]);
    return (_jsx(FormRow, { children: _jsxs(Stack, { gap: 2, flexDirection: "column", children: [photoState.status === 'starting' && (_jsx(Stack, { direction: "row", gap: 2, alignItems: "center", justifyContent: "center", paddingY: 4, width: "100%", children: _jsx(Text, { variant: "body-small", color: "n300", textAlign: "center", children: i18next.t($ => $.stat.kinderConnectCapture.businessLogic.src.translations.json.photoCapture
                            .startingCamera) }) })), photoState.status === 'error' && (_jsxs(Stack, { gap: 2, alignItems: "center", paddingY: 2, justifyContent: "space-between", children: [_jsx(Text, { variant: "body-small", color: "r400", children: i18next.t($ => $.stat.kinderConnectCapture.businessLogic.src.translations.json.photoCapture
                                .cameraError) }), _jsx(Button, { variant: "transparent", size: "compact", isDisabled: isDisabled, "data-e2e-id": kinderConnectPhotoRetryButtonE2eId, "aria-label": i18next.t($ => $.stat.kinderConnectCapture.businessLogic.src.translations.json.photoCapture
                                .retryAriaLabel), icon: "refresh", onClick: startWebCamera })] })), !isNative && photoState.status === 'previewing' && (_jsx(Box, { borderRadius: "8px", overflow: "hidden", maxWidth: "100%", position: "relative", children: _jsx(Box, { component: "video", ref: videoCallbackRef, autoPlay: true, playsInline: true, muted: true, width: "100%", height: "100%", objectFit: "cover", "aria-label": i18next.t($ => $.stat.kinderConnectCapture.businessLogic.src.translations.json.photoCapture
                            .captureAriaLabel) }) })), photoState.status === 'captured' && (_jsx(Box, { borderRadius: "8px", overflow: "hidden", children: _jsx(Box, { component: "img", src: photoState.dataUrl, alt: i18next.t($ => $.stat.kinderConnectCapture.businessLogic.src.translations.json.photoCapture
                            .captureAriaLabel), width: "100%" }) })), _jsxs(Stack, { direction: "row", gap: 2, width: "100%", children: [isNative && photoState.status === 'previewing' && (_jsx(Button, { fullWidth: true, variant: "tertiary", isDisabled: isDisabled, "data-e2e-id": kinderConnectPhotoCaptureButtonE2eId, onClick: handleNativeCapture, text: i18next.t($ => $.stat.kinderConnectCapture.businessLogic.src.translations.json.photoCapture
                                .takePhoto) })), !isNative && photoState.status === 'previewing' && (_jsx(Button, { fullWidth: true, size: "compact", variant: "primary", icon: "camera", isDisabled: isDisabled, "data-e2e-id": kinderConnectPhotoCaptureButtonE2eId, onClick: () => {
                                if (videoNodeRef.current) {
                                    handleWebCapture(videoNodeRef.current);
                                }
                            }, text: i18next.t($ => $.stat.kinderConnectCapture.businessLogic.src.translations.json.photoCapture
                                .capture) })), photoState.status === 'captured' && (_jsx(Button, { variant: "tertiary", size: "compact", icon: "replay", isDisabled: isDisabled, "data-e2e-id": kinderConnectPhotoRetakeButtonE2eId, onClick: handleRetake, fullWidth: true, text: i18next.t($ => $.stat.kinderConnectCapture.businessLogic.src.translations.json.photoCapture.retake) }))] })] }) }));
};
